<template>
  <div
    class="form-component w-full flex flex-col items-start gap-0.5"
    :class="{ 'with-error': state.isDirty && !state.isValid }">
    <label
      v-if="props.label.length > 0"
      :for="props.id"
      class="w-full !text-sm !text-gray !font-normal"
      :class="{ '!text-red': state.isDirty && !state.isValid }">
      {{ props.label }}
    </label>

    <input
      type="text"
      :id="props.id"
      :name="props.name"
      :placeholder="props.placeholder"
      class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full bg-transparent"
      :class="{ '!border-red': state.isDirty && !state.isValid, 'pointer-events-none': props.isReadOnly }"
      :readonly="props.isReadOnly"
      v-model="state.numberField"
      @input="onInput"
      @change="onChange" />

    <div
      v-if="state.isDirty && !state.isValid"
      class="text-sm text-red">
      <span
        v-for="error in state.errors"
        :key="error"
        >{{ error }}</span
      >
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, watch } from 'vue';

const props = defineProps({
  label: { type: String, required: true },
  id: { type: String, required: true },
  name: { type: String, required: true },
  placeholder: { type: String, required: false },
  value: { type: String, required: true },
  isRequired: { type: Boolean, required: true },
  isReadOnly: { type: Boolean, required: false },
  minLength: { type: Number, required: false },
  maxLength: { type: Number, required: false },
  triggerValidation: { type: Number, required: false },
  triggerResetValidation: { type: Number, required: false },
  serverError: { type: String, required: false },
});

const emit = defineEmits(['update:value', 'check-is-valid', 'on-change']);

const state = reactive({
  numberField: '',
  isDirty: false,
  isValid: false,
  errors: [] as string[],
});

const phoneRegex = /^[0-9()+\-\s]+$/;

const validate = () => {
  state.isDirty = true;
  state.errors = [];

  if (props.serverError?.length) {
    state.errors.push(props.serverError);
  }

  if (props.isRequired && !state.numberField.trim()) {
    state.errors.push('This field is required. Please fill it in.');
  }

  if (props.minLength && state.numberField.length < props.minLength) {
    state.errors.push(`Minimum length is ${props.minLength} characters.`);
  }

  if (props.maxLength && state.numberField.length > props.maxLength) {
    state.errors.push(`Maximum length is ${props.maxLength} characters.`);
  }

  if (state.numberField.length > 0 && !phoneRegex.test(state.numberField)) {
    state.errors.push('Please enter a valid phone number. Only numbers, +, (, ), -, and spaces are allowed.');
  }

  state.isValid = state.errors.length === 0;
  emit('check-is-valid', { name: props.name, isValid: state.isValid });
};

const reset = () => {
  state.isDirty = false;
  state.errors = [];
  state.isValid = false;
  emit('check-is-valid', { name: props.name, isValid: state.isValid });
};

const onInput = () => {
  validate();
  emit('update:value', state.numberField);
  emit('on-change');
};

const onChange = () => {
  emit('on-change');
};

onMounted(() => {
  state.numberField = props.value ?? '';
});

watch(
  () => props.value,
  (newVal) => {
    if (newVal !== state.numberField) {
      state.numberField = newVal ?? '';
      validate();
    }
  }
);

watch(
  () => props.triggerValidation,
  () => {
    validate();
  }
);

watch(
  () => props.triggerResetValidation,
  () => {
    reset();
  }
);

watch(
  () => props.serverError,
  () => {
    validate();
  }
);
</script>
